import React, { memo } from 'react';
import { useTranslations } from 'next-intl';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { cn } from '@wwe-portal/ui/lib/lib';

/**
 * Render Wrestler Class and Era { title: '', image: '' }
 * @param key {string}
 */

interface WrestlerClassEraProps {
  wClass: string;
  wEra: string;
  wStable: string[];
}

const renderWrestlerClassEra = (key: string) => {
  switch (key) {
    case 'Color_Yellow':
      return {
        title: 'showboats',
        image: '/assets/images/superstar/showboats.webp',
      };
    case 'Color_Blue':
      return {
        title: 'acrobats',
        image: '/assets/images/superstar/acrobats.webp',
      };
    case 'Color_Red':
      return {
        title: 'powerhouses',
        image: '/assets/images/superstar/powerhouses.webp',
      };
    case 'Color_Green':
      return {
        title: 'technicians',
        image: '/assets/images/superstar/technicians.webp',
      };
    case 'Color_Purple':
      return {
        title: 'tricksters',
        image: '/assets/images/superstar/tricksters.webp',
      };
    case 'Color_Black':
      return {
        title: 'strikers',
        image: '/assets/images/superstar/strikers.webp',
      };
    case 'Era_Attitude':
      return {
        title: 'attitude',
        image: '/assets/images/superstar/attitude.webp',
      };
    case 'Era_Classic':
      return {
        title: 'legends',
        image: '/assets/images/superstar/legends.webp',
      };
    case 'Era_Modern':
      return {
        title: 'modern',
        image: '/assets/images/superstar/modern.webp',
      };
    case 'Era_NewGen':
      return {
        title: 'new_generation',
        image: '/assets/images/superstar/newGeneration.webp',
      };
    case 'Era_Reality':
      return {
        title: 'reality',
        image: '/assets/images/superstar/reality.webp',
      };
    case 'Era_PG':
      return {
        title: 'pg',
        image: '/assets/images/superstar/PG.webp',
      };
    case 'Era_RuthlessAggression':
      return {
        title: 'ruthless_aggression',
        image: '/assets/images/superstar/ruthlessAggression.webp',
      };
    // Era_HallofFame_LO or Era_HallofFame or every other Era
    default:
      return {
        title: 'hall_Of_fame',
        image: '/assets/images/superstar/hallOfFame.webp',
      };
  }
};

/**
 * Render Wrestler Stable  { title: '', image: '' }
 * @param key {string}
 */
const renderWrestlerStable = (key: string) => {
  switch (key) {
    case 'Stable_Alliance':
      return {
        title: 'alliance',
        image: '/assets/images/superstar/Alliance.webp',
      };
    case 'Stable_Authority':
      return {
        title: 'authority',
        image: '/assets/images/superstar/Authority.webp',
      };
    case 'Stable_Club':
      return {
        title: 'club',
        image: '/assets/images/superstar/Club.webp',
      };
    case 'Stable_CORP':
      return {
        title: 'corp',
        image: '/assets/images/superstar/CORP.webp',
      };
    case 'Stable_DiamondExchange':
      return {
        title: 'diamond_exchange',
        image: '/assets/images/superstar/DiamondExchange.webp',
      };
    case 'Stable_DX':
      return {
        title: 'dx',
        image: '/assets/images/superstar/DX.webp',
      };
    case 'Stable_Evolution':
      return {
        title: 'evolution',
        image: '/assets/images/superstar/Evolution.webp',
      };
    case 'Stable_FourHorsemen':
      return {
        title: 'four_horsemen',
        image: '/assets/images/superstar/FourHorsemen.webp',
      };
    case 'Stable_HartFoundation':
      return {
        title: 'hart_foundation',
        image: '/assets/images/superstar/HartFoundation.webp',
      };
    case 'Stable_HeenanFamily':
      return {
        title: 'heenan_family',
        image: '/assets/images/superstar/HeenanFamily.png',
      };
    case 'Stable_LeagueOfNations':
      return {
        title: 'league_of_nations',
        image: '/assets/images/superstar/LeagueOfNations.webp',
      };
    case 'Stable_Legacy':
      return {
        title: 'legacy',
        image: '/assets/images/superstar/Legacy.webp',
      };
    case 'Stable_MillionairesClub':
      return {
        title: 'millionaires_club',
        image: '/assets/images/superstar/MillionairesClub.webp',
      };
    case 'Stable_Nexus':
      return {
        title: 'nexus',
        image: '/assets/images/superstar/Nexus.png',
      };
    case 'Stable_NOD':
      return {
        title: 'nod',
        image: '/assets/images/superstar/NOD.png',
      };
    case 'Stable_NWO':
      return {
        title: 'nwo',
        image: '/assets/images/superstar/NWO.webp',
      };
    case 'Stable_RatPack':
      return {
        title: 'rat_pack',
        image: '/assets/images/superstar/RatPack.png',
      };
    case 'Stable_Shield':
      return {
        title: 'shield',
        image: '/assets/images/superstar/Shield.webp',
      };
    case 'Stable_Z':
      return {
        title: 'z',
        image: '/assets/images/superstar/Z.webp',
      };
    default:
      return null;
  }
};

function WrestlerClassEra({ wClass, wEra, wStable }: WrestlerClassEraProps) {
  /**
   * Use translation hook
   * @see https://react.i18next.com/
   */
  const t = useTranslations();
  /**
   * Use window size custom hook
   */
  const { isMobile } = useResponsive();

  return (
    <div className="relative w-full">
      <div
        className={cn('mb-4 flex', {
          'border-back200 border-b-[1px] pb-4': isMobile,
        })}
      >
        <div className="mr-2.5 flex h-12 w-12 items-center justify-center rounded-full bg-[#272b4a]">
          <img src={renderWrestlerClassEra(wClass).image} alt="class" />
        </div>
        <div>
          <div className="text-[#a0aec0]">
            {t(renderWrestlerClassEra(wClass).title)}
          </div>
          <span className="text-xs text-[#7f9cf5]">{t('class')}</span>
        </div>
      </div>
      <div
        className={cn('mb-4 flex', {
          'border-back200 border-b-[1px] pb-4': isMobile,
        })}
      >
        <div className="mr-2.5 flex h-12 w-12 items-center justify-center rounded-full bg-[#272b4a]">
          <img src={renderWrestlerClassEra(wEra).image} alt="era" />
        </div>
        <div>
          <div className="text-[#a0aec0]">
            {t(renderWrestlerClassEra(wEra).title)}
          </div>
          <span className="text-xs text-[#7f9cf5]">{t('era')}</span>
        </div>
      </div>
      {wStable &&
        wStable.map(
          (stable) =>
            renderWrestlerStable(stable) && (
              <div
                key={stable}
                className={`mb-4 ${isMobile && 'wrestler-details-hr'} flex`}
              >
                <div className="mr-2.5 flex h-12 w-12 items-center justify-center rounded-full bg-[#272b4a]">
                  <img src={renderWrestlerStable(stable)?.image} alt="stable" />
                </div>
                <div>
                  <div className="text-textColor3">
                    {t(renderWrestlerStable(stable)?.title)}
                  </div>
                  <span className="text-xs text-[#7f9cf5]">{t('stable')}</span>
                </div>
              </div>
            )
        )}
    </div>
  );
}

export default memo(WrestlerClassEra);
